import { useCallback, useState } from 'react'
import { Upload, Loader2, AlertCircle } from 'lucide-react'
import { cn } from '@/utils/cn'

interface FileUploadDropzoneProps {
  onFileSelect: (file: File) => void
  isLoading?: boolean
  error?: string | null
  accept?: string
  maxSizeMB?: number
}

export function FileUploadDropzone({
  onFileSelect,
  isLoading = false,
  error = null,
  accept = '.csv,.xls,.xlsx',
  maxSizeMB = 10,
}: FileUploadDropzoneProps) {
  const [isDragging, setIsDragging] = useState(false)

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(false)

      const files = e.dataTransfer.files
      if (files && files.length > 0) {
        onFileSelect(files[0])
      }
    },
    [onFileSelect]
  )

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files
      if (files && files.length > 0) {
        onFileSelect(files[0])
      }
      // Reset input value so the same file can be selected again
      e.target.value = ''
    },
    [onFileSelect]
  )

  return (
    <div
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      className={cn(
        'relative flex flex-col items-center justify-center gap-3 p-8',
        'border-2 border-dashed rounded-[12px] transition-all',
        isDragging
          ? 'border-primary bg-accent'
          : 'border-[#D6DAE1] bg-[#F8F8F8] hover:border-[#9CA3AF]',
        isLoading && 'pointer-events-none opacity-70'
      )}
    >
      {/* Icon */}
      <div
        className={cn(
          'flex items-center justify-center w-12 h-12 rounded-full',
          isDragging ? 'bg-primary text-white' : 'bg-[#E5E7EB] text-[#556374]'
        )}
      >
        {isLoading ? (
          <Loader2 className="h-6 w-6 animate-spin" />
        ) : (
          <Upload className="h-6 w-6" />
        )}
      </div>

      {/* Text */}
      <div className="text-center">
        <label className="cursor-pointer">
          <span className="text-[14px] font-medium text-primary hover:underline">
            Upload a file
          </span>
          <input
            type="file"
            accept={accept}
            onChange={handleFileInputChange}
            className="hidden"
            disabled={isLoading}
          />
        </label>
        <span className="text-[14px] text-[#556374]"> or drag and drop</span>
      </div>

      {/* File type info */}
      <p className="text-[13px] text-[#9CA3AF]">
        CSV or XLS up to {maxSizeMB}MB
      </p>

      {/* Error message */}
      {error && (
        <div className="flex items-center gap-2 text-[13px] text-red-500 mt-1">
          <AlertCircle className="h-4 w-4" />
          <span>{error}</span>
        </div>
      )}
    </div>
  )
}
